import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Button, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AmoTableWithFilters from "components/table/AmoTableWithFilters";
import { useTreasurerUserHooks } from "./hooks/userDataHooks";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useMunicipalContext } from "contexts/MunicipalContext";
import { useSnackbar } from "contexts/SnackbarContext";
import { displayTypes, tableDefaultActions } from "constants/amoTableConstants";
import colors from "constants/colors";
import { errorMessages } from "constants/errorMessages";
import { snackbarTypes } from "constants/snackbar";
import { formatISODateDashStyle } from "utils/date";
import { exportToExcel } from "utils/export";
import { formatToPhoneWithExtension } from "utils/number";
import { booleanToTextOrUndefined } from "utils/string";
import CheckedIcon from "./CheckedIcon";

const useStyles = makeStyles((theme) => ({
  email: {
    color: colors.green.main,
    textDecoration: "none",
    justifyContent: "flex-start",
    fontWeight: "bold",
    marginLeft: "-0.375rem",
    paddingTop: "0",
    paddingBottom: "0",
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "transparent",
      cursor: "pointer",
    },
  },
}));

/**
 * A table component for displaying Treasurer users
 *
 * @param {object} props
 * @returns {React.Component} The table component
 */
const TreasurerUserTable = (props) => {
  const classes = useStyles();
  const { id, tableTitleComponent } = props;

  const history = useHistory();
  const location = useLocation();
  const globalClasses = useGlobalStyles();
  const { showSnackbar } = useSnackbar();
  const { getMunicipalitiesNames } = useMunicipalContext();

  const [focusedItem, setFocusedItem] = useState({});
  const rowRefs = useRef({});
  const filteredData = useRef([]);

  const { data: userData, isFetching: dataFetching } = useTreasurerUserHooks();

  const zoomString = (phone, phoneExtension) => {
    const cleanPhone = phone ? phone.replace(/[^0-9.]+/g, "") : "";
    const cleanExtension = phoneExtension
      ? `?callerid={${phoneExtension}}`
      : "";

    return `zoomphonecall://+1${cleanPhone}${cleanExtension}`;
  };

  const tableColumns = [
    {
      fieldKey: "name",
      label: "Name",
      minWidth: "11rem",
      customRender: (user) => (
        <Link
          to={`/users/amo/${user.id}${location.search}`}
          ref={(element) => {
            rowRefs.current[user.id] = element;
          }}
          onFocus={() => setFocusedItem({ id: user.id })}
          className={globalClasses.tableActionCell}
        >
          {user.name}
        </Link>
      ),
      isCustom: true,
    },
    {
      fieldKey: "municipality",
      label: "Municipality",
      minWidth: "11rem",
      dropdownOptions: getMunicipalitiesNames(),
      displayType: displayTypes.dropdown,
    },
    {
      fieldKey: "email",
      label: "Email",
      minWidth: "10rem",
      customRender: (item) => (
        <Button
          className={classes.email}
          align="left"
          href={`mailto:${item.email}`}
          disableRipple
          style={{ backgroundColor: "transparent" }}
          onClick={(elem) => elem.stopPropagation()}
        >
          {item.email}
        </Button>
      ),
      isCustom: true,
    },
    {
      fieldKey: "phone",
      label: "Phone #",
      minWidth: "8rem",
      customRender: (item) => (
        <Button
          className={classes.email}
          align="left"
          href={zoomString(item.phone, item.phoneExtension)}
          disableRipple
          style={{ backgroundColor: "transparent" }}
          onClick={(elem) => elem.stopPropagation()}
        >
          {formatToPhoneWithExtension(item.phone, item.phoneExtension)}
        </Button>
      ),
      isCustom: true,
    },
    {
      fieldKey: "lastLoginDate",
      label: "Last Login",
      customRender: (item) =>
        item.lastLoginDate !== "1970-01-01T00:00:00" ? (
          <Typography>
            <Grid item xs>
              {formatISODateDashStyle(item.lastLoginDate)}{" "}
            </Grid>
          </Typography>
        ) : (
          <></>
        ),
      isCustom: true,
      isDate: true,
      displayType: displayTypes.date,
    },
    {
      fieldKey: "enabled",
      label: "Enabled",
      customRender: (item) => <CheckedIcon checked={item.enabled} />,
      customExport: (item) => booleanToTextOrUndefined(item.enabled) ?? "",
      displayType: displayTypes.boolean,
      isCustom: true,
      horizontalAlign: "center",
    },
  ];

  const handleItemClick = (user) => {
    history.push(`${`/users/treasurer/${user.id}`}${location.search}`);
  };

  const tableActions = {
    titleComponent: tableTitleComponent,
    actions: [tableDefaultActions.search, tableDefaultActions.export],
    actionsCustomConfigs: {
      [tableDefaultActions.export]: {
        onClick: () => {
          try {
            exportToExcel(
              filteredData.current,
              tableColumns,
              "Municipal Treasurers"
            );
          } catch (error) {
            console.error(error);

            showSnackbar(
              errorMessages.creatingSpreadsheet,
              snackbarTypes.error
            );
          }
        },
      },
    },
  };

  return (
    <AmoTableWithFilters
      id={id}
      testId="treasurerUserTable"
      items={dataFetching ? [] : userData}
      columns={tableColumns}
      defaultOrderBy="name"
      enableItemClick
      onItemClick={handleItemClick}
      onFilteredDataChanged={(value) => {
        filteredData.current = value;
      }}
      onScroll={(item) => {
        rowRefs.current?.[item.id]?.focus();
      }}
      focusedItem={focusedItem}
      actionsProps={tableActions}
      isLoading={dataFetching}
    />
  );
};

// set the prop-types for this component
TreasurerUserTable.propTypes = {
  id: PropTypes.string,
  tableTitleComponent: PropTypes.node,
};

TreasurerUserTable.defaultProps = {
  id: "treasurer-user-table",
  tableTitleComponent: null,
};

export default TreasurerUserTable;
